// src/pages/UserDetails.js

import React, { useEffect, useState } from "react";
import api from "../services/api";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, Paper, Typography, Button, Toolbar as MuiToolbar, TextField, Grid, MenuItem } from "@mui/material";
import ImageGalleryDropdown from "../components/ImageGalleryDropdown";
import { RoleMapping } from "../constants/roles";
import RecordNavigator from "../components/RecordNavigator";
import { formatPhoneNumber } from "../utils/phoneUtils";

const UserDetails = () => {
    const { id } = useParams(); // route: /users/:id
    const location = useLocation();
    const users = location.state?.records || [];
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [editedUser, setEditedUser] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [passwordUpdated, setPasswordUpdated] = useState(false);

    useEffect(() => {
        async function fetchUserDetails() {
            try {
                const token = localStorage.getItem("token");
                const response = await api.get(`/users/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUser(response.data);
                setEditedUser(response.data);
            } catch (error) {
                console.error("Error fetching user details:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchUserDetails();
    }, [id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedUser((prev) => ({ ...prev, [name]: value }));
        if (name === 'password') {
            setPasswordUpdated(true); // Flag indicating password changed
        }
    };

    const handleEditClick = async () => {
        if (isEditing) {
            try {
                const token = localStorage.getItem("token");
                const updatePayload = {
                    name: editedUser.name,
                    email: editedUser.email,
                    phone: editedUser.phone,
                    username: editedUser.username,
                    role: editedUser.role,
                    comments: editedUser.comments,
                    ...(passwordUpdated && { password: editedUser.password }),
                };
                //console.log("Update Payload:", updatePayload);

                const response = await api.put(`/users/${id}`, updatePayload, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (response.data) {

                    setUser(prev => ({
                        ...response.data,
                        bucket: prev.bucket || response.data.bucket,
                    }));

                    setEditedUser(prev => ({
                        ...response.data,
                        bucket: prev.bucket || response.data.bucket,
                    }));

                    setIsEditing(false);
                    setPasswordUpdated(false);
                } else {
                    console.error("No updated data received from API.");
                }
            } catch (error) {
                console.error("Error updating user:", error);
            }
        } else {
            setIsEditing(true);
        }
    };

    const handleDeleteClick = () => {
        if (window.confirm("Are you sure you want to delete this user?")) {
            const token = localStorage.getItem("token");
            api
                .delete(`/users/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
                .then(() => navigate("/users"))
                .catch((error) => console.error("Error deleting user:", error));
        }
    };

    if (loading) return <Typography>Loading user details...</Typography>;
    if (!user) return <Typography>User not found.</Typography>;

    const disabledInputStyles = {
        "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: "black",
        },
    };

    return (
        <>
            <MuiToolbar />
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h5" gutterBottom sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>User Details</span>
                        <RecordNavigator records={users} currentId={id} basePath="/users" />
                    </Typography>
                    <Box sx={{ maxWidth: '70%', mx: 'auto' }}>
                        <Grid container spacing={2} justifyContent="center">
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Name"
                                    name="name"
                                    value={editedUser.name || ""}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isEditing}
                                    sx={disabledInputStyles}
                                />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    label="Email"
                                    name="email"
                                    value={editedUser.email || ""}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isEditing}
                                    sx={disabledInputStyles}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="Phone"
                                    name="phone"
                                    value={formatPhoneNumber(editedUser.phone) || ""}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isEditing}
                                    sx={disabledInputStyles}
                                />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    label="Username"
                                    name="username"
                                    value={editedUser.username || ""}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isEditing}
                                    sx={disabledInputStyles}
                                />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    label="Password"
                                    name="password"
                                    type={isEditing ? "password" : "text"}
                                    value={isEditing ? (editedUser.password || "") : "•••••••••••"}
                                    onChange={(e) => {
                                        handleInputChange(e);
                                        setPasswordUpdated(true);
                                    }}
                                    onFocus={(e) => e.target.select()} // <- This will select all dots on focus
                                    helperText={isEditing ? "Enter new password to update." : ""}
                                    disabled={!isEditing}
                                    margin="normal"
                                    fullWidth
                                    sx={disabledInputStyles}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    select
                                    label="Role"
                                    name="role"
                                    value={editedUser.role || ""}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    disabled={!isEditing}
                                    sx={disabledInputStyles}
                                >
                                    {Object.entries(RoleMapping).map(([key, label]) => (
                                        <MenuItem key={key} value={Number(key)}>{label}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                <TextField
                                    label="Comments"
                                    name="comments"
                                    value={editedUser.comments || ""}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="normal"
                                    multiline
                                    rows={2}
                                    disabled={!isEditing}
                                    sx={disabledInputStyles}
                                />
                            </Grid>
                            <Grid item xs={12} sm={1} sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ImageGalleryDropdown
                                    images={editedUser.bucket?.images || []}
                                    onUploadSuccess={(newImageData) => {
                                        setEditedUser(prev => ({
                                            ...prev,
                                            bucket: {
                                                ...prev.bucket,
                                                images: [...(prev.bucket?.images || []), newImageData]
                                            }
                                        }));
                                    }}
                                    onDelete={(imageId) => {
                                        api.delete(`/images/${imageId}`)
                                            .then(() => {
                                                setEditedUser(prev => ({
                                                    ...prev,
                                                    bucket: {
                                                        ...prev.bucket,
                                                        images: prev.bucket.images.filter(img => img.id !== imageId)
                                                    }
                                                }));
                                            })
                                            .catch(error => {
                                                console.error("Error deleting image:", error);
                                            });
                                    }}
                                    userId={user.id}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', maxWidth: '60%', mx: 'auto' }}>
                        <Button variant="contained" onClick={handleEditClick}>{isEditing ? "Save" : "Edit"}</Button>
                        <Button variant="outlined" color="error" sx={{ ml: 2 }} onClick={handleDeleteClick}>Delete</Button>
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

export default UserDetails;